import { get, post, put, del } from '@/services/http';
import { ReportModel } from '@/models/report';
import { MeasureModel } from '@/models/measure';
import { DimensionModel } from '@/models/dimension';
import { FilterColumnModel } from '@/models/filter_column';
import { FilterColumnList } from '@/collections/filter_columns';
import { InventoryParam } from '@/models/inventory_param';
import { ProductSegmentModel } from '@/models/product_segment';
import dateFormat from 'dateformat';
import { SupplierProductModel } from '@/models/supplier_product';

export const PAYMENT_METHOD_OPTIONS = [
  { text: 'Bank Transfer / Wire', value: 'bank_transfer' },
  { text: 'Credit Card', value: 'credit_card' },
  { text: 'Check', value: 'check' },
  { text: 'PayPal', value: 'paypal' },
  { text: 'Cash on Delivery', value: 'cash_on_delivery' },
  { text: 'Letter of Credit', value: 'letter_of_credit' },
  { text: 'Other', value: 'other' }
];
import { ReorderSettingParam } from '@/models/reorder_setting_param';
import { ParamList } from '@/collections/params';
import { ParamModel } from './param';

export interface GlobalDefaults {
  leadtime?: number | null;
  orderCycle?: number | null;
  minStockCoverDays?: number | null;
}

export interface SupplierParams {
  leadtime: number;
  orderCycle: number;
  minStockCoverDays: number;
  minOrderValue: number;
  maxOrderValue: number;
  minOrderQuantity: number;
  maxOrderQuantity: number;
  scope: 'product' | 'variant';
}

export interface ISupplierMetric {
  value: number;
  label: string;
  trend: 'up' | 'down' | 'stable';
}

export interface ISupplierRecommendation {
  reason: string;
  priority: 'high' | 'medium' | 'low';
  metrics: {
    lowStockProducts: number;
    outOfStockProducts: number;
    revenueAtRisk: number;
    lastOrderDate: string;
  };
}

export interface ISupplierAssignByConditions {
  enabled: boolean;
  conditions: FilterColumnModel[];
  productSegment: string | null;
  lastRunAt: string | null;
  lastRunResult: {
    created: number;
    removed: number;
    skipped: number;
    errors: string[];
  } | null;
}

export interface ISupplier {
  id: number;
  name: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  minOrderValue: number;
  maxOrderValue: number;
  minInventoryValue: number;
  maxInventoryValue: number | null;
  leadTime: number;
  reorderCycle?: number;
  minStockCoverDays?: number;
  status: string;
  summaryMetrics: ISupplierMetric[];
  recommendation?: ISupplierRecommendation;
  productCount: number;
  buyingListCount: number;
  minOrderQuantity: number;
  maxOrderQuantity?: number | null;
  fixedOrderQuantity?: number;
  scope?: string;
  options?: any;
  currency_code?: string | null;
  contactName?: string;
  additionalEmails?: string[];
  notes?: string;
  paymentMethod?: string | null;
  bankDetails?: Record<string, string>;
  specialTerms?: string;
}

export interface IMockSupplierMetric {
  value: string;
  label: string;
  trend: {
    value: number;
    isPositive: boolean;
  };
}

export interface IMockSupplierRecommendation {
  reason: string;
  priority: 'high' | 'medium' | 'low';
  metrics: {
    lowStockProducts: number;
    outOfStockProducts: number;
    revenueAtRisk: number;
    lastOrderDate: string;
  };
}

export interface IMockSupplierResponse {
  supplier: {
    id: number;
    name: string;
    contact_email: string;
    contact_phone: string;
    address: string;
    min_order_value: number;
    max_order_value: number;
    min_inventory_value: number;
    max_inventory_value: number;
    payment_terms: string;
    lead_time: number;
    safety_stock_percentage: number;
    performance_metrics: IMockSupplierMetric[];
    recommendation: IMockSupplierRecommendation;
    product_count: number;
    buying_list_count: number;
    min_order_quantity: number;
    max_order_quantity: number;
    fixed_order_quantity: number;
    scope: string;
    options: any;
  };
}

interface SettingHistory {
  date: string;
  productSegment: ProductSegmentModel;
  filterColumns: FilterColumnList;
  inventoryParam: InventoryParam;
}

interface PageOptions {
  sortBy?: any;
  sortDesc?: any;
  page?: string;
  itemsPerPage?: string;
  multiSort?: boolean;
}

interface SettingScope {
  product: string;
  location: string;
  parameter: string;
  supplier: string;
}

export interface IOptimizedProductsOptions {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: Record<string, any>;
  /**
   * Drop variants the merchant excluded from replenishment in Reorder
   * Parameters. Opt-in: the supplier screens manage those products and must
   * keep listing them, so only the PO buying surfaces pass it.
   */
  excludeReplenishmentExcluded?: boolean;
}

/**
 * Formatted supplier product for display in the frontend
 */
export interface ISupplierProductDisplay {
  /**
   * The supplier-product LINK id (AS-10941). The endpoint returns it — it is
   * what bulk-update-settings and reset-cost are keyed by — but the type
   * omitted it, so a row reaching the reset without one was only discoverable
   * at runtime. Nullable: rows the list maps straight from report data have
   * no link yet.
   */
  id?: number | null;
  variant_id: string;
  image: string;
  'Item[Item Description]': string;
  variant: string;
  product_sku: string;
  product_barcode: string;
  'Vendor Name': string;
  supplier: number;
  supplier_sku: string;
  unit_cost: number;
  '[vendor_lead_time]': number;
  '[reorder_cycle]': number;
  '[min_order_quantity]': number;
  '[max_order_quantity]': number;
  '[fixed_order_quantity]': number;
  '[replenishment_exclusion]': number;
  '[min_stock_cover_days]': number;
  '[max_stock_cover_days]': number;
  '[min_inventory_quantity]': number;
  '[max_inventory_quantity]': number;
  status: string;
  isPreferred: boolean;
  linkType: 'manual' | 'auto';
  createdAt: string;
  updatedAt: string;
}

export interface IOptimizedProductsResult {
  products: ISupplierProductDisplay[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ISearchVariantsOptions {
  page?: number;
  limit?: number;
  search?: string;
}

/**
 * Variant search result with supplier assignment information
 */
export interface ISearchVariant {
  variantId: number;
  productName: string;
  variantTitle: string;
  sku: string;
  barcode: string;
  vendor: string;
  imageUrl: string;
  suppliers: IVariantSupplierInfo[];
  hasSuppliers: boolean;
  isAssignedToCurrentSupplier: boolean;
}

/**
 * Supplier assignment information for a variant
 */
export interface IVariantSupplierInfo {
  supplierId: number;
  supplierName: string;
  supplierSku: string;
  cost: number;
  leadTime: number;
  orderCycle: number;
  minOrderQuantity: number;
  maxOrderQuantity: number;
  fixedOrderQuantity: number;
  isPreferred: boolean;
  isCurrentSupplier: boolean;
}

export interface ISearchVariantsResult {
  variants: ISearchVariant[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Request parameters for fetching optimized products
 */
export interface IOptimizedProductsRequestParams {
  page: number;
  limit: number;
  search: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  [key: string]: any; // Allow additional filter parameters
}

/**
 * Request parameters for searching variants
 */
export interface ISearchVariantsRequestParams {
  page: number;
  limit: number;
  search: string;
}

/**
 * Common pagination response structure
 */
export interface IPaginationResponse {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/**
 * API response for fetching optimized products
 */
export interface IOptimizedProductsApiResponse {
  data: {
    products: ISupplierProductDisplay[];
    pagination: IPaginationResponse;
  };
}

/**
 * API response for searching variants
 */
export interface ISearchVariantsApiResponse {
  data: {
    variants: ISearchVariant[];
    pagination: IPaginationResponse;
  };
}

export interface IBulkSupplierInfoUpdate {
  id: number;
  name?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
}

export interface IBulkSupplierInfoResult {
  success: number;
  failed: number;
  results: Array<{ id: number; [k: string]: any }>;
  errors: Array<{ update: { id?: number; [k: string]: any }; error: string }>;
}

export class SupplierModel {

  public static async getById(id: number): Promise<SupplierModel> {
    // Implementation to fetch supplier by ID
    // Example:
    const response: any = await get(`/suppliers/${id}`);
    const data = response.data;
    return new SupplierModel(data);
  }

  public static async bulkUpdateInfo(updates: IBulkSupplierInfoUpdate[]): Promise<IBulkSupplierInfoResult> {
    const response: any = await post('/suppliers/bulk-update-info', { updates });
    return response.data as IBulkSupplierInfoResult;
  }

  public static async previewAutoAssignVendor(): Promise<{ suppliers: any[]; totals: any }> {
    const response: any = await get(`/suppliers/auto-assign-vendor/preview`);
    return response.data || response;
  }

  public static async applyAutoAssignVendor(
    conflictMode: 'skip' | 'overwrite',
    supplierIds?: number[],
  ): Promise<{ applied: number; skipped: number; failed: number; results: any[]; errors: any[] }> {
    const payload: any = { conflictMode };
    if (supplierIds && supplierIds.length) { payload.supplierIds = supplierIds; }
    const response: any = await post(`/suppliers/auto-assign-vendor/apply`, payload);
    return response.data?.data || response.data || response;
  }
  public id: number;
  public name: string;
  public status: string;
  public contactEmail: string;
  public contactPhone: string;
  public address: string;
  public minOrderValue: number;
  public maxOrderValue: number;
  public minInventoryValue: number;
  public maxInventoryValue: number | null;
  public leadTime: number;
  public reorderCycle?: number;
  public minStockCoverDays?: number;
  public summaryMetrics: ISupplierMetric[];
  public recommendation?: ISupplierRecommendation;
  public productCount: number;
  public buyingListCount: number;
  public minOrderQuantity: number;
  public maxOrderQuantity?: number | null;
  public fixedOrderQuantity?: number;
  public scope?: string;
  public options?: any;
  public currency_code?: string | null;
  public contactName: string;
  public additionalEmails: string[];
  public notes: string;
  public paymentMethod: string | null;
  public bankDetails: Record<string, string>;
  public specialTerms: string;
  constructor(data: Partial<ISupplier> | null) {
    this.id = data?.id || 0;
    this.status = data?.status || '';
    this.name = data?.name || '';
    this.contactEmail = data?.contactEmail || '';
    this.contactPhone = data?.contactPhone || '';
    this.address = data?.address || '';
    this.minOrderValue = data?.minOrderValue || 0;
    this.maxOrderValue = data?.maxOrderValue || 0;
    this.minInventoryValue = data?.minInventoryValue || 0;
    this.maxInventoryValue = data?.maxInventoryValue ?? null;
    this.leadTime = data?.leadTime || 0;
    this.reorderCycle = data?.reorderCycle;
    this.minStockCoverDays = data?.minStockCoverDays;
    this.summaryMetrics = data?.summaryMetrics || [];
    this.recommendation = data?.recommendation;
    this.productCount = data?.productCount || 0;
    this.buyingListCount = data?.buyingListCount || 0;
    this.minOrderQuantity = data?.minOrderQuantity || 0;
    this.maxOrderQuantity = data?.maxOrderQuantity;
    this.fixedOrderQuantity = data?.fixedOrderQuantity;
    this.scope = data?.scope;
    this.options = data?.options;
    this.currency_code = data?.currency_code || null;
    this.contactName = data?.contactName || '';
    this.additionalEmails = data?.additionalEmails || [];
    this.notes = data?.notes || '';
    this.paymentMethod = data?.paymentMethod || null;
    this.bankDetails = data?.bankDetails || {};
    this.specialTerms = data?.specialTerms || '';
  }

  public async fetch(): Promise<void> {
    try {
      const response: any = await get(`/suppliers/${this.id}`);
      if (response.data) {
        this.mapData(response.data);
      }
    } catch (error) {
      console.error('Error fetching supplier:', error);
      throw error;
    }
  }

  public async save(): Promise<void> {
    try {
      const payload = {
        name: this.name,
        contactEmail: this.contactEmail,
        contactPhone: this.contactPhone,
        address: this.address,
        minOrderValue: this.minOrderValue,
        maxOrderValue: this.maxOrderValue,
        minInventoryValue: this.minInventoryValue,
        maxInventoryValue: this.maxInventoryValue,
        leadTime: this.leadTime,
        reorderCycle: this.reorderCycle,
        minStockCoverDays: this.minStockCoverDays,
        status: this.status,
        minOrderQuantity: this.minOrderQuantity,
        maxOrderQuantity: this.maxOrderQuantity,
        fixedOrderQuantity: this.fixedOrderQuantity,
        scope: this.scope,
        options: this.options,
        currency_code: this.currency_code,
        contactName: this.contactName,
        additionalEmails: this.additionalEmails,
        notes: this.notes,
        paymentMethod: this.paymentMethod,
        bankDetails: this.bankDetails,
        specialTerms: this.specialTerms,
      };

      const response: any = await post('/suppliers', payload);
      if (response.data) {
        this.id = response.data.id;
        this.mapData(response.data);
      }
    } catch (error) {
      console.error('Error saving supplier:', error);
      throw error;
    }
  }

  public async update(options?: { forceUpdate?: boolean }): Promise<void> {
    try {
      const payload = {
        name: this.name,
        contactEmail: this.contactEmail,
        contactPhone: this.contactPhone,
        address: this.address,
        minOrderValue: this.minOrderValue,
        maxOrderValue: this.maxOrderValue,
        minInventoryValue: this.minInventoryValue,
        maxInventoryValue: this.maxInventoryValue,
        leadTime: this.leadTime,
        reorderCycle: this.reorderCycle,
        minStockCoverDays: this.minStockCoverDays,
        minOrderQuantity: this.minOrderQuantity,
        maxOrderQuantity: this.maxOrderQuantity,
        fixedOrderQuantity: this.fixedOrderQuantity,
        scope: this.scope,
        options: this.options,
        status: this.status,
        currency_code: this.currency_code,
        contactName: this.contactName,
        additionalEmails: this.additionalEmails,
        notes: this.notes,
        paymentMethod: this.paymentMethod,
        bankDetails: this.bankDetails,
        specialTerms: this.specialTerms,
        // Pass forceUpdate flag to backend - backend will auto-update reorder param settings
        forceUpdate: options?.forceUpdate || false,
      };

      const response: any = await put(`/suppliers/${this.id}`, payload);
      if (response.data) {
        this.mapData(response.data);
      }
    } catch (error) {
      console.error('Error updating supplier:', error);
      throw error;
    }
  }

  /**
   * Force update only the specified params to all variants
   * Only sends the provided fields to the backend to prevent overwriting other variant values
   */
  public async forceUpdateParams(params: {
    leadTime?: number | null;
    reorderCycle?: number | null;
    minStockCoverDays?: number | null;
    minOrderValue?: number | null;
    maxOrderValue?: number | null;
    minOrderQuantity?: number | null;
    maxOrderQuantity?: number | null;
    scope?: string | null;
  }): Promise<void> {
    try {
      // Build payload with only the specified fields
      const payload: any = {
        forceUpdate: true
      };

      if (params.leadTime !== null && params.leadTime !== undefined) {
        payload.leadTime = params.leadTime;
      }
      if (params.reorderCycle !== null && params.reorderCycle !== undefined) {
        payload.reorderCycle = params.reorderCycle;
      }
      if (params.minStockCoverDays !== null && params.minStockCoverDays !== undefined) {
        payload.minStockCoverDays = params.minStockCoverDays;
      }
      if (params.minOrderValue !== null && params.minOrderValue !== undefined) {
        payload.minOrderValue = params.minOrderValue;
      }
      if (params.maxOrderValue !== null && params.maxOrderValue !== undefined) {
        payload.maxOrderValue = params.maxOrderValue;
      }
      if (params.minOrderQuantity !== null && params.minOrderQuantity !== undefined) {
        payload.minOrderQuantity = params.minOrderQuantity;
      }
      if (params.maxOrderQuantity !== null && params.maxOrderQuantity !== undefined) {
        payload.maxOrderQuantity = params.maxOrderQuantity;
      }
      if (params.scope !== null && params.scope !== undefined) {
        payload.scope = params.scope;
      }

      const response: any = await put(`/suppliers/${this.id}`, payload);
      if (response.data) {
        this.mapData(response.data);
      }
    } catch (error) {
      console.error('Error force updating supplier params:', error);
      throw error;
    }
  }

  public async remove(): Promise<void> {
    try {
      await del(`/suppliers/${this.id}`);
    } catch (error) {
      console.error('Error removing supplier:', error);
      throw error;
    }
  }

  public async triggerAssignByConditions(): Promise<{ created: number; removed: number; skipped: number; errors: string[] }> {
    try {
      const response: any = await post(`/suppliers/${this.id}/assign-by-conditions/run`);
      return response.data?.data || response.data || response;
    } catch (error) {
      console.error('Error triggering assign by conditions:', error);
      throw error;
    }
  }

  public async previewAssignByConditions(conditions: any[]): Promise<any> {
    const response: any = await post(`/suppliers/${this.id}/assign-by-conditions/preview`, { conditions });
    return response.data || response;
  }

  public async getSupplierDetails(): Promise<void> {
    try {
      await this.fetch();
      // Get supplier metrics
      // const metricsResponse: any = await get(`/api/suppliers/${this.id}/metrics`);
      // if (metricsResponse.data) {
      //   this.summaryMetrics = this.transformMetrics(metricsResponse.data);
      // }
    } catch (error) {
      console.error('Error fetching supplier details:', error);
      throw error;
    }
  }

  public getMetricTrend(metric: ISupplierMetric): string {
    if (!metric.trend) { return 'mdi-minus'; }
    return metric.trend === 'up' ? 'mdi-arrow-up' : 'mdi-arrow-down';
  }

  public getMetricColor(metric: ISupplierMetric): string {
    if (!metric.trend) { return 'grey'; }
    return metric.trend === 'up' ? 'success' : 'error';
  }

  public toJSON(): ISupplier {
    return {
      id: this.id,
      name: this.name,
      contactEmail: this.contactEmail,
      contactPhone: this.contactPhone,
      address: this.address,
      minOrderValue: this.minOrderValue,
      maxOrderValue: this.maxOrderValue,
      minInventoryValue: this.minInventoryValue,
      maxInventoryValue: this.maxInventoryValue,
      leadTime: this.leadTime,
      reorderCycle: this.reorderCycle,
      minOrderQuantity: this.minOrderQuantity,
      maxOrderQuantity: this.maxOrderQuantity,
      fixedOrderQuantity: this.fixedOrderQuantity,
      scope: this.scope,
      options: this.options,
      status: this.status,
      summaryMetrics: this.summaryMetrics,
      recommendation: this.recommendation,
      productCount: this.productCount,
      buyingListCount: this.buyingListCount,
      currency_code: this.currency_code,
      contactName: this.contactName,
      additionalEmails: this.additionalEmails,
      notes: this.notes,
      paymentMethod: this.paymentMethod,
      bankDetails: this.bankDetails,
      specialTerms: this.specialTerms,
    };
  }

  public async fetchProducts(measureCode: string = 'replenishment_settings', operator?: 'eq' | 'ne' | 'all' | 'blank', initialFilterColumns: FilterColumnList = new FilterColumnList()): Promise<ReportModel> {
    try {
      const report = new ReportModel();

      // Set up measure
      report.measure = new MeasureModel({ code: measureCode });

      // Set up dimension
      report.dimension = new DimensionModel({ code: 'by_product' });

      // Set report type
      report.isDetail = true;
      report.isMetric = false;
      report.page = 1;
      report.perPage = 100;

      // Set report columns
      const reportColumns: any = [];
      // Basic product details
      reportColumns.push({ code: 'product_id', dataType: 'text', name: 'Product Id' });
      reportColumns.push({ code: 'Variant Name', dataType: 'text', name: 'Product Variant' });
      reportColumns.push({ code: 'Item[Item Description]', dataType: 'text', name: 'Product' });
      reportColumns.push({ code: 'supplier', dataType: 'text', name: 'Supplier' });
      reportColumns.push({ code: 'supplier_id', dataType: 'text', name: 'Supplier Id' });
      reportColumns.push({ code: 'Vendor Name', dataType: 'text', name: 'Vendor' });



      // Stock parameters
      reportColumns.push({ code: '[vendor_lead_time]', dataType: 'decimal', name: 'Vendor Lead Time' });
      reportColumns.push({ code: '[min_stock_cover_days]', dataType: 'decimal', name: 'Min Stock Cover Days' });
      reportColumns.push({ code: '[max_stock_cover_days]', dataType: 'decimal', name: 'Max Stock Cover Days' });
      reportColumns.push({ code: '[min_inventory_quantity]', dataType: 'decimal', name: 'Min Inventory Quantity' });
      reportColumns.push({ code: '[max_inventory_quantity]', dataType: 'decimal', name: 'Max Inventory Quantity' });
      reportColumns.push({ code: '[min_order_quantity]', dataType: 'decimal', name: 'Min Order Quantity' });
      reportColumns.push({ code: '[max_order_quantity]', dataType: 'decimal', name: 'Max Order Quantity' });
      reportColumns.push({ code: '[fixed_order_quantity]', dataType: 'decimal', name: 'Fixed Order Quantity' });
      reportColumns.push({ code: '[reorder_cycle]', dataType: 'number', name: 'Reorder Cycle' });
      if (measureCode === 'replenishment_settings') {
        reportColumns.push({ code: '[replenishment_exclusion]', dataType: 'number', name: 'Replenishment Exclusion' });
      }




      report.reportColumns = reportColumns;

      const filterColumns = new FilterColumnList();
      // Add initial filter columns
      for (const filterColumn of initialFilterColumns.items) {
        filterColumns.add(filterColumn);
      }

      // Only filter by supplier if operator is provided
      if (operator !== 'all' && operator !== 'blank') {

        const supplierFilter = new FilterColumnModel();
        supplierFilter.mapData({
          name: 'SupplierId',
          code: 'supplier_id',
          value: [this.id],
          operator,
          dataType: 'number',
        });
        filterColumns.add(supplierFilter);
      } else if (operator === 'blank') {
        const supplierFilter = new FilterColumnModel();
        supplierFilter.mapData({
          name: 'SupplierId',
          code: 'supplier_id',
          operator: 'null',
          dataType: 'number',
          value: [0],
        });
        filterColumns.add(supplierFilter);
      }
      report.filterColumns = filterColumns;

      // Get report data
      await report.getReport(false);

      return report;

    } catch (error) {
      console.error('Error fetching supplier products:', error);
      throw error;
    }
  }

  public async fetchVariants(measureCode: string = 'replenishment_settings', operator?: 'eq' | 'ne' | 'all' | 'blank', initialFilterColumns: FilterColumnList = new FilterColumnList()): Promise<ReportModel> {
    try {
      const report = new ReportModel();

      // Set up measure
      report.measure = new MeasureModel({ code: measureCode });

      // Set up dimension
      report.dimension = new DimensionModel({ code: 'by_variant' });

      // Set report type
      report.isDetail = true;
      report.isMetric = false;
      report.page = 1;
      report.perPage = 100;

      // Set report columns
      const reportColumns: any = [];
      // Basic product details
      reportColumns.push({ code: 'variant_id', dataType: 'text', name: 'Variant Id' });
      reportColumns.push({ code: 'product_id', dataType: 'text', name: 'Product Id' });
      reportColumns.push({ code: 'Variant Name', dataType: 'text', name: 'Product Variant' });
      reportColumns.push({ code: 'image', dataType: 'image', name: 'Product Image' });
      reportColumns.push({ code: 'Item[Item Description]', dataType: 'text', name: 'Product' });
      reportColumns.push({ code: 'variant', dataType: 'text', name: 'Variant' });
      reportColumns.push({ code: 'product_sku', dataType: 'text', name: 'SKU' });
      reportColumns.push({ code: 'product_barcode', dataType: 'text', name: 'Barcode' });
      reportColumns.push({ code: 'supplier', dataType: 'text', name: 'Supplier' });
      reportColumns.push({ code: 'supplier_id', dataType: 'text', name: 'Supplier Id' });
      reportColumns.push({ code: 'Vendor Name', dataType: 'text', name: 'Vendor' });



      // Stock parameters
      reportColumns.push({ code: '[vendor_lead_time]', dataType: 'decimal', name: 'Vendor Lead Time' });
      reportColumns.push({ code: '[min_stock_cover_days]', dataType: 'decimal', name: 'Min Stock Cover Days' });
      reportColumns.push({ code: '[max_stock_cover_days]', dataType: 'decimal', name: 'Max Stock Cover Days' });
      reportColumns.push({ code: '[min_inventory_quantity]', dataType: 'decimal', name: 'Min Inventory Quantity' });
      reportColumns.push({ code: '[max_inventory_quantity]', dataType: 'decimal', name: 'Max Inventory Quantity' });
      reportColumns.push({ code: '[min_order_quantity]', dataType: 'decimal', name: 'Min Order Quantity' });
      reportColumns.push({ code: '[max_order_quantity]', dataType: 'decimal', name: 'Max Order Quantity' });
      reportColumns.push({ code: '[fixed_order_quantity]', dataType: 'decimal', name: 'Fixed Order Quantity' });
      reportColumns.push({ code: '[reorder_cycle]', dataType: 'number', name: 'Reorder Cycle' });
      if (measureCode === 'replenishment_settings') {
        reportColumns.push({ code: '[replenishment_exclusion]', dataType: 'number', name: 'Replenishment Exclusion' });
      }

      report.reportColumns = reportColumns;

      const filterColumns = new FilterColumnList();
      // Add initial filter columns
      for (const filterColumn of initialFilterColumns.items) {
        filterColumns.add(filterColumn);
      }

      // Only filter by supplier if operator is provided
      if (operator !== 'all' && operator !== 'blank') {

        const supplierFilter = new FilterColumnModel();
        supplierFilter.mapData({
          name: 'SupplierId',
          code: 'supplier_id',
          value: [this.id],
          operator,
          dataType: 'number',
        });
        filterColumns.add(supplierFilter);
      } else if (operator === 'blank') {
        const supplierFilter = new FilterColumnModel();
        supplierFilter.mapData({
          name: 'SupplierId',
          code: 'supplier_id',
          operator: 'null',
          dataType: 'number',
          value: [0],
        });
        filterColumns.add(supplierFilter);
      }
      report.filterColumns = filterColumns;

      // Get report data
      await report.getReport(false);

      return report;

    } catch (error) {
      console.error('Error fetching supplier products:', error);
      throw error;
    }
  }


  // True when the caller's initialReportParams already carry an explicit
  // suggest_future_reorder_quantity (the "Ignore reorder point condition" user param).
  // The backend's param substitution is first-occurrence-wins, so auto-adding our own
  // copy FIRST would silently override the user's explicit choice — skip it instead.
  private hasExplicitSuggestFutureParam(initialReportParams: any): boolean {
    const items = initialReportParams?.items
      || (Array.isArray(initialReportParams) ? initialReportParams : []);
    return items.some((p: any) => (p?.code || '') === 'suggest_future_reorder_quantity');
  }

  public allowSuggestFutureReorderQuantity(): ParamList {
    const paramList = new ParamList();
    const param: ParamModel = new ParamModel();
    param.mapData({
      name: 'suggest_future',
      code: 'suggest_future_reorder_quantity',
      valueType: 'single',
      value: ['yes'],
      dataType: 'text',
    });
    paramList.add(param);
    return paramList;
  }

  // US-105 "Show only urgent reorders" ROW filters. Kept as helpers so
  // fetchProductsWithOptions and countProductsWithOptions stay in lockstep
  // (a divergence would make the count disagree with the listed rows).
  // [days_to_reorder] <= 0 → due now (independent of the suggest-future param).
  private urgentDueNowFilter(): FilterColumnModel {
    const filter = new FilterColumnModel();
    filter.mapData({
      name: 'Days to reorder',
      code: '[days_to_reorder]',
      operator: 'lte',
      value: [0],
      dataType: 'number',
    });
    return filter;
  }

  // [inventory_replenishment] > 0 → has a real quantity to order.
  private urgentHasQuantityFilter(): FilterColumnModel {
    const filter = new FilterColumnModel();
    filter.mapData({
      name: 'Reorder Quantity',
      code: '[inventory_replenishment]',
      operator: 'gt',
      value: [0],
      dataType: 'number',
    });
    return filter;
  }

  public async fetchProductsWithOptions(measureCode: string = 'replenishment_settings', operator?: 'eq' | 'ne' | 'all' | 'null', options: any = {}): Promise<ReportModel> {
    try {
      const {
        initialFilterColumns, pageOptions, dateRange, productSegment,
        suggestFutureReorderQuantity, showNeedReorderNowOnly, supplierIdColumn, columns,
        initialReportParams, skipped,
        // Pass 2 (AS-9964): additional report inputs forwarded from the
        // source ReportPage so the by_product expansion reproduces the same
        // numbers the user sees on the source report.
        analyticsType, comparisonDate, pivotOption,
      } = options;
      const report = new ReportModel();
      const filterColumns = new FilterColumnList();
      // Add initial filter columns
      for (const filterColumn of initialFilterColumns.items) {
        filterColumns.add(filterColumn);
      }
      // Set up measure
      report.measure = new MeasureModel({ code: measureCode });

      // Set up dimension
      report.dimension = new DimensionModel({ code: 'by_variant' });
      report.reportParams = new ParamList();
      // Apply the optional Pass 2 inputs. Guard with truthiness so legacy
      // callers that omit these stay byte-for-byte identical.
      if (analyticsType) { report.analyticsType = analyticsType; }
      if (comparisonDate) { report.comparisonDate = comparisonDate; }
      if (pivotOption) { report.pivotOption = pivotOption; }
      // suggest_future_reorder_quantity is the look-ahead FORMULA param — it is
      // independent of the "show only urgent" ROW filter below.
      if (suggestFutureReorderQuantity) {
        // Skip the auto 'yes' when the user set the param explicitly — first-wins
        // substitution would otherwise discard their choice (e.g. explicit 'no').
        if (!this.hasExplicitSuggestFutureParam(initialReportParams)) {
          report.reportParams = this.allowSuggestFutureReorderQuantity();
        }
      }
      // "Show only urgent reorders" is a ROW filter, applied independently of the
      // suggest-future param. Urgent = due now AND has something to order, so we
      // AND two conditions (US-105):
      //   [days_to_reorder] <= 0        — due now. Survives suggest_future='yes',
      //                                   unlike the qty-only proxy (which matches
      //                                   every product once look-ahead is on).
      //   [inventory_replenishment] > 0 — a real quantity to order; drops due-but-
      //                                   capped/covered rows a buyer can't act on.
      if (showNeedReorderNowOnly && measureCode === 'inventory_replenishment') {
        filterColumns.add(this.urgentDueNowFilter());
        filterColumns.add(this.urgentHasQuantityFilter());
      }
      // parse initialReportParams and add to report.reportParams
      if (initialReportParams) {
        // Handle both ParamList objects and plain objects with items property
        const paramsToAdd = initialReportParams.items || (initialReportParams instanceof Array ? initialReportParams : []);
        for (const param of paramsToAdd) {
          report.reportParams.add(param);
        }
      }

      // Set report type
      report.isDetail = true;
      report.isMetric = false;
      if (pageOptions.page) {
        report.page = pageOptions.page;
      }
      if (pageOptions.itemsPerPage) {
        report.perPage = pageOptions.itemsPerPage;
      }
      if (pageOptions.sortBy) {
        report.sortBy = pageOptions.sortBy;
      }
      if (pageOptions.sortDesc) {
        report.sortDesc = pageOptions.sortDesc;
      }
      if (dateRange) {
        report.filterTimerange = [dateRange.start, dateRange.end];
      }
      if (productSegment) {
        report.productSegment = productSegment;
      }

      // Set report columns
      let reportColumns: any = [];
      if (columns) {
        reportColumns = columns;
      } else {
        reportColumns.push({ code: 'variant_id', dataType: 'number', name: 'Variant ID' });
        reportColumns.push({ code: '[average_daily_units_sold_param]', dataType: 'number', name: 'Units Sold Per Day' });
        reportColumns.push({ code: '[sold_quantity_param]', dataType: 'number', name: 'Units Sold' });
        reportColumns.push({ code: '[stock_days_inventory_planning]', dataType: 'number', name: 'Inventory Remaining Days' });
        reportColumns.push({ code: '[inventory_replenishment]', dataType: 'number', name: 'Reorder Quantity' });
        reportColumns.push({ code: '[reorder_date]', dataType: 'number', name: 'Reorder Date' });
        reportColumns.push({ code: 'product_barcode', dataType: 'string', name: 'Barcode' });
        reportColumns.push({ code: '[incoming_inventory_quantity]', dataType: 'number', name: 'Incoming Inventory' });
        //reportColumns.push({ code: '[buying_list_quantity]', dataType: 'number', name: 'Buying List Quantity' });
        reportColumns.push({ code: '[unit_cost]', dataType: 'number', name: 'Unit Cost' });
      }
      /** filter by supplier_ids to get the list of products not belong to the current supplier */
      if (supplierIdColumn) {
        reportColumns.push({ code: 'suppliers', dataType: 'string', name: 'Suppliers' });
      } else {
      /** get products of current suppliers */
        reportColumns.push({ code: 'supplier', dataType: 'string', name: 'Supplier' });
        reportColumns.push({ code: 'supplier_id', dataType: 'number', name: 'Supplier ID' });
      }

      report.reportColumns = reportColumns;


      // Only filter by supplier if operator is provided
      if (operator !== 'all') {

        const supplierFilter = new FilterColumnModel();
        supplierFilter.mapData({
          name: supplierIdColumn || 'SupplierId',
          code: supplierIdColumn || 'supplier_id',
          value: [this.id],
          operator,
          dataType: 'number',
        });
        filterColumns.add(supplierFilter);
      }
      report.filterColumns = filterColumns;

      // Get report data
      if (!skipped) {
        await report.getReport(false);
      }
      return report;

    } catch (error) {
      console.error('Error fetching supplier products:', error);
      throw error;
    }
  }

  public async countProductsWithOptions(measureCode: string = 'replenishment_settings', operator?: 'eq' | 'ne' | 'all' | 'null', options: any = {}): Promise<any> {
    try {
      const { initialFilterColumns, pageOptions, dateRange, productSegment, suggestFutureReorderQuantity, showNeedReorderNowOnly, supplierIdColumn, initialReportParams} = options;
      const report = new ReportModel();
      const filterColumns = new FilterColumnList();
      // Add initial filter columns
      for (const filterColumn of initialFilterColumns.items) {
        filterColumns.add(filterColumn);
      }
      // Set up measure
      report.measure = new MeasureModel({ code: measureCode });

      // Set up dimension
      report.dimension = new DimensionModel({ code: 'by_variant' });

      // Set report type
      report.isDetail = true;
      report.isMetric = false;
      report.page = -1;
      report.perPage = 50;
      report.reportParams = new ParamList();
      // suggest_future_reorder_quantity is the look-ahead FORMULA param — it is
      // independent of the "show only urgent" ROW filter below.
      if (suggestFutureReorderQuantity) {
        // Skip the auto 'yes' when the user set the param explicitly — first-wins
        // substitution would otherwise discard their choice (e.g. explicit 'no').
        if (!this.hasExplicitSuggestFutureParam(initialReportParams)) {
          report.reportParams = this.allowSuggestFutureReorderQuantity();
        }
      }
      // "Show only urgent reorders" ROW filters — must stay in lockstep with
      // fetchProductsWithOptions so the count matches the listed rows (US-105).
      if (showNeedReorderNowOnly && measureCode === 'inventory_replenishment') {
        filterColumns.add(this.urgentDueNowFilter());
        filterColumns.add(this.urgentHasQuantityFilter());
      }
      if (productSegment) {
        report.productSegment = productSegment;
      }
      if (dateRange) {
        report.filterTimerange = [dateRange.start, dateRange.end];
      }
      
      // parse initialReportParams and add to report.reportParams
      if (initialReportParams) {
        // Handle both ParamList objects and plain objects with items property
        const paramsToAdd = initialReportParams.items || (initialReportParams instanceof Array ? initialReportParams : []);
        for (const param of paramsToAdd) {
          report.reportParams.add(param);
        }
      }
      
      // Set report columns
      const reportColumns: any = [];
      reportColumns.push({ code: 'variant_id', dataType: 'number', name: 'Variant ID' });
      // reportColumns.push({ code: '[average_daily_units_sold_param]', dataType: 'number', name: 'Units Sold Per Day' });
      // reportColumns.push({ code: '[sold_quantity_param]', dataType: 'number', name: 'Units Sold' });
      // reportColumns.push({ code: '[stock_days_inventory_planning]', dataType: 'number', name: 'Inventory Remaining Days' });
      // reportColumns.push({ code: '[inventory_replenishment]', dataType: 'number', name: 'Reorder Quantity' });
      // reportColumns.push({ code: '[reorder_date]', dataType: 'number', name: 'Reorder Date' });
      // reportColumns.push({ code: 'product_barcode', dataType: 'string', name: 'Barcode' });
      // reportColumns.push({ code: '[incoming_inventory_quantity]', dataType: 'number', name: 'Incoming Inventory' });
      // reportColumns.push({ code: '[buying_list_quantity]', dataType: 'number', name: 'Buying List Quantity' });

      /** filter by supplier_ids to get the list of products not belong to the current supplier */
      if (supplierIdColumn) {
        reportColumns.push({ code: 'suppliers', dataType: 'string', name: 'Suppliers' });
      } else {
        /** get products of current suppliers */
        reportColumns.push({ code: 'supplier', dataType: 'string', name: 'Supplier' });
        reportColumns.push({ code: 'supplier_id', dataType: 'number', name: 'Supplier ID' });
      }

      report.reportColumns = reportColumns;



      // Only filter by supplier if operator is provided
      if (operator !== 'all') {

        const supplierFilter = new FilterColumnModel();
        supplierFilter.mapData({
          name: supplierIdColumn || 'SupplierId',
          code: supplierIdColumn || 'supplier_id',
          value: [this.id],
          operator,
          dataType: 'number',
        });
        filterColumns.add(supplierFilter);
      }
      report.filterColumns = filterColumns;

      // Get report data
      await report.getCount(false);

      return {totalItems: report.total};

    } catch (error) {
      console.error('Error fetching supplier products:', error);
      throw error;
    }
  }

  public async addProducts(products: any[], filterData: any): Promise<void> {
    try {
      // 1. Create supplier-product relationships in parallel
      const variants = products.map((product) => ({
        variantId: product.variant_id,
        supplierSku: product.product_sku,
        cost: product.unit_cost || 0,
        replenishmentExclusion: product['[replenishment_exclusion]'] || false,
        leadTime: parseInt(product['[vendor_lead_time]']) || 0,
        orderCycle: parseInt(product['[reorder_cycle]']) || 0,
        fixOrderQuantity: parseInt(product['[fixed_order_quantity]']) || 0,
        minimumOrderQuantity: parseInt(product['[min_order_quantity]']) || 0,
        maxOrderQuantity: parseInt(product['[max_order_quantity]']) || 0,
        productInfo: {
          productName: product['Item[Item Description]'] || '',
          variantTitle: product.variant || '',
          sku: product.product_sku || '',
          barcode: product.product_barcode || '',
          vendor: product['Vendor Name'] || '',
          imageUrl: product.image || '',
        },
        productSetting: {
          minInventoryQuantity: parseInt(product['[min_inventory_quantity]']) || 0,
          maxInventoryQuantity: parseInt(product['[max_inventory_quantity]']) || 0,
          minStockCoverDays: parseInt(product['[min_stock_cover_days]']) || 0,
          maxStockCoverDays: parseInt(product['[max_stock_cover_days]']) || 0,
        },
      }));

      // Create supplier-product relationships
      const response: any = await post('/supplier-products/supplier/add-variants', {
        supplierId: this.id,
        variants,
      });
      return response;
    } catch (error) {
      console.error('Error adding products:', error);
      throw error;
    }
  }

  public async addAllProducts(products: any[], filterData: any): Promise<void> {
    try {
      // 1. First save to replenishment_settings (existing flow)
      const report = new ReportModel();
      report.measure = new MeasureModel({code: 'replenishment_settings'});
      report.dimension = new DimensionModel({code: 'by_variant'});
      report.isDetail = true;

      // Create filter columns based on selection type
      const filterColumns = new FilterColumnList();

      // Using report filters
      for (const filter of filterData.filterColumns.items) {
        filterColumns.add(filter);
      }
      report.productSegment = filterData.productSegment;

      // add supplier id to the filter column to identify the available supplier products
      const supplierFilter = new FilterColumnModel({
        name: 'SupplierId',
        code: 'supplier_id',
        operator: 'ne',
        dataType: 'number',
        value: [this.id],
      });
      filterColumns.add(supplierFilter);
      report.filterColumns = filterColumns;
      // generate new report params
      const addAllProductsReportParams = report.generateParams();

      // Create supplier-product relationships
      const response: any = await post('/supplier-products/supplier/add-all-variants', {
          supplierId: this.id,
          reportOptions: addAllProductsReportParams,
        });
      return response;
    } catch (error) {
      console.error('Error adding products:', error);
      throw error;
    }
  }

  public async removeProducts(products: any[]): Promise<void> {
    if (!products || products.length === 0) {
      return;
    }
    try {
      // Create a new report model to generate params
      const report = new ReportModel();
      report.measure = new MeasureModel({code: 'replenishment_settings'});
      report.dimension = new DimensionModel({code: 'by_variant'});
      report.isDetail = true;

      // Create filter columns based on selection type
      const filterColumns = new FilterColumnList();

      const variantFilter = new FilterColumnModel({
        name: 'Variant Id',
        code: 'variant_id',
        operator: 'eq',
        dataType: 'number',
        value: products.map((p) => p.variant_id),
      });
      filterColumns.add(variantFilter);

      // add supplier id to the filter column to identify the available supplier products
      // just enable if allow multiple suppliers per variant
      // const supplierFilter = new FilterColumnModel({
      //   name: 'Supplier ID',
      //   code: 'supplier_id',
      //   operator: 'eq',
      //   dataType: 'number',
      //   value: [this.id]
      // });
      // filterColumns.add(supplierFilter);

      report.filterColumns = filterColumns;

      // Create and save inventory param with supplier id
      const inventoryParam = new InventoryParam();
      inventoryParam.supplierId = this.id;

      // Save using report params
      const reportParams = report.generateParams();
      const resetFields = ['supplier_id'];

      // Create instance of SupplierProductModel to use removeByVariantIds
      const supplierProduct = new SupplierProductModel(null);

      // remove from supplier products
      await supplierProduct.removeByVariantIds(this.id, products.map((p) => p.variant_id));

    } catch (error) {
      console.error('Error removing products:', error);
      throw error;
    }
  }

  private transformMetrics(apiMetrics: any[]): ISupplierMetric[] {
    return apiMetrics.map((metric) => ({
      value: metric.value,
      label: metric.metricType,
      trend: this.calculateTrend(metric.value, metric.previousValue),
    }));
  }

  private calculateTrend(currentValue: number, previousValue: number): 'up' | 'down' | 'stable' {
    if (!previousValue) { return 'stable'; }
    if (currentValue > previousValue) { return 'up'; }
    if (currentValue < previousValue) { return 'down'; }
    return 'stable';
  }

  private mapData(data: any): void {
    this.name = data.name;
    this.contactEmail = data.contactEmail;
    this.contactPhone = data.contactPhone;
    this.address = data.address;
    this.minOrderValue = data.minOrderValue;
    this.maxOrderValue = data.maxOrderValue;
    this.minInventoryValue = data.minInventoryValue;
    this.maxInventoryValue = data.maxInventoryValue;
    this.leadTime = data.leadTime;
    this.reorderCycle = data.reorderCycle;
    this.minStockCoverDays = data.minStockCoverDays;
    this.minOrderQuantity = data.minOrderQuantity;
    this.maxOrderQuantity = data.maxOrderQuantity;
    this.fixedOrderQuantity = data.fixedOrderQuantity;
    this.scope = data.scope;
    this.options = data.options;
    if (data.metrics) {
      this.summaryMetrics = this.transformMetrics(data.metrics);
    }
    this.status = data.status;
    this.productCount = data.productCount;
    this.buyingListCount = data.buyingListCount;
    this.currency_code = data.currency_code || null;
    this.contactName = data.contactName || '';
    this.additionalEmails = data.additionalEmails || [];
    this.notes = data.notes || '';
    this.paymentMethod = data.paymentMethod || null;
    this.bankDetails = data.bankDetails || {};
    this.specialTerms = data.specialTerms || '';
  }

  /**
   * Optimized method to fetch supplier products using direct API call
   * instead of complex report generation. Improves performance from 3-8s to <200ms.
   */
  public async fetchProductsOptimized(options: IOptimizedProductsOptions = {}): Promise<IOptimizedProductsResult> {
    try {
      const params: IOptimizedProductsRequestParams = {
        page: options.page || 1,
        limit: options.limit || 25,
        search: options.search || '',
        sortBy: options.sortBy || 'product_name',
        sortOrder: options.sortOrder || 'asc',
      };

      // Sent as a string: the backend reads `=== 'true'` off the query string.
      if (options.excludeReplenishmentExcluded) {
        params.excludeReplenishmentExcluded = 'true';
      }

      // Add filters if provided
      if (options.filters) {
        Object.assign(params, options.filters);
      }

      // Pass params directly, not wrapped in { params }
      const response = await get<IOptimizedProductsApiResponse>(`/supplier-products/supplier/${this.id}`, params);

      return {
        products: response.data.products || [],
        pagination: {
          page: response.data.pagination?.page || 1,
          limit: response.data.pagination?.limit || 25,
          total: response.data.pagination?.total || 0,
          totalPages: response.data.pagination?.totalPages || 0,
        },
      };
    } catch (error) {
      console.error('Error fetching optimized supplier products:', error);
      throw error;
    }
  }

  /**
   * Search available product variants for Add Product Modal
   * Uses Shopify GraphQL for fast product discovery with supplier assignment info
   */
  public async searchAvailableVariants(options: ISearchVariantsOptions = {}): Promise<ISearchVariantsResult> {
    try {
      const params: ISearchVariantsRequestParams = {
        page: options.page || 1,
        limit: options.limit || 25,
        search: options.search || '',
      };

      // Pass params directly, not wrapped in { params }
      const response = await get<ISearchVariantsApiResponse>(`/supplier-products/supplier/${this.id}/search-variants`, params);
      
      return {
        variants: response.data.variants || [],
        pagination: {
          page: response.data.pagination?.page || 1,
          limit: response.data.pagination?.limit || 25,
          total: response.data.pagination?.total || 0,
          totalPages: response.data.pagination?.totalPages || 0,
        },
      };
    } catch (error) {
      console.error('Error searching available variants:', error);
      throw error;
    }
  }

  private async saveSupplierParams() {
    const reorderSettingParam = new ReorderSettingParam({
      supplierId: this.id,
      leadtime: this.leadTime,
      orderCycle: this.reorderCycle,
      minOrderQuantity: this.minOrderQuantity,
      maxOrderQuantity: this.maxOrderQuantity,
      scope: this.scope,
    });

    const settingScope: SettingScope = {
      parameter: 'supplier',
      supplier: 'supplier',
      product: 'product',
      location: 'location',
    };

    // Create filter columns based on selection type
    const filterColumns = new FilterColumnList();
    const supplierFilter = new FilterColumnModel({
      name: 'Supplier ID',
      code: 'supplier_id',
      operator: 'eq',
      dataType: 'number',
      value: [this.id],
    });
    filterColumns.add(supplierFilter);

    // Save using report params
    await reorderSettingParam.saveReorder(filterColumns, {}, settingScope);
  }
}
